import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Carousel } from '@comeon/ui';

import Icon, { ICON_LEFT, ICON_RIGHT } from '~/components/icon/index.js';
import Banner from '~/components/banner/banner.tsx';
import getBannerHeight from '~/components/banner/utils/get-banner-height.ts';
import { eCommerceCampaignImp, eCommerceCampaignClick } from '~/components/promotions/ducks/index';
import { isSmallerSelector } from '~/components/layout/mediaquery.js';

import useTermsAndConditions from './terms-and-conditions/use-terms-and-conditions.js';
import CarouselItem from './carousel-item.js';
import TermsAndConditions from './terms-and-conditions/terms-and-conditions.js';
import { SMALLER_HEIGHT, BIGGER_HEIGHT } from './carousel-view.js';
import CarouselSkeleton, { CarouselContainer } from './carousel-skeleton.js';
import { selectEnableBannerService } from '~/components/brand/ducks/selectors';

const carouselItemClick = (title, id, index, url, externalWindow) => {
	eCommerceCampaignClick(title, id, index);
	window.open(url, externalWindow ? '_blank' : '_self');
};

const NewCarousel = props => {
	const isSmall = useSelector(isSmallerSelector);
	const { isTermsConditionsEnabled, termsConditions } = useTermsAndConditions();
	const isBannerServiceEnabled = useSelector(selectEnableBannerService);

	const height = useMemo(() => {
		if (isBannerServiceEnabled) {
			return getBannerHeight(isSmall, props.campaigns);
		}
		return isSmall ? SMALLER_HEIGHT : BIGGER_HEIGHT;
	}, [isBannerServiceEnabled, isSmall, props.campaigns]);

	// TODO: skip checking file and path explicitly when the API supports returning only valid objects
	const multiLayerCampaigns = useMemo(
		() =>
			isBannerServiceEnabled
				? null
				: props.campaigns?.filter(
						campaign =>
							campaign.backgroundImage?.path &&
							campaign.backgroundImage?.file &&
							campaign.foregroundImage?.path &&
							campaign.foregroundImage?.file
				  ),
		[props.campaigns, isBannerServiceEnabled]
	);

	const carouselNodes = useMemo(
		() =>
			isBannerServiceEnabled
				? props.campaigns.map((data, index) => <Banner key={data.id} data={data} index={index} />)
				: multiLayerCampaigns?.map(
						(
							{
								id,
								backgroundImage,
								foregroundImage,
								backgroundImageURL,
								foregroundImageURL,
								externalWindow,
								buttonText,
								headerText,
								messageText,
								link
							},
							index
						) => (
							<CarouselItem
								id={id}
								index={index}
								key={id}
								backgroundImage={backgroundImage}
								foregroundImage={foregroundImage}
								backgroundImageURL={backgroundImageURL}
								foregroundImageURL={foregroundImageURL}
								externalWindow={externalWindow}
								buttonText={buttonText}
								headerText={headerText}
								messageText={messageText}
								link={link}
								carouselItemClick={carouselItemClick}
								isSmall={isSmall}
							/>
						)
				  ),
		[isBannerServiceEnabled, multiLayerCampaigns, isSmall, props.campaigns]
	);

	if (!carouselNodes?.length) {
		if (!props.isHydrated) {
			return (
				<CarouselSkeleton
					isSmall={isSmall}
					isTermsConditionsEnabled={isTermsConditionsEnabled}
					height={isBannerServiceEnabled ? height : undefined}
				/>
			);
		}
		return null;
	}

	eCommerceCampaignImp(isBannerServiceEnabled ? props.campaigns : multiLayerCampaigns);

	return (
		<CarouselContainer height={height} isSmall={isSmall} isTermsConditionsEnabled={isTermsConditionsEnabled}>
			<Carousel
				iconLeft={<Icon type={ICON_LEFT} className="carousel__carousel-arrow" />}
				iconRight={<Icon type={ICON_RIGHT} className="carousel__carousel-arrow" />}
				showArrows={!isSmall}
				arrowBackground={!isSmall}
				height={height}
				showDots={!isBannerServiceEnabled}
				disableSwipe={!isSmall}
			>
				{carouselNodes}
			</Carousel>

			{!isBannerServiceEnabled && isTermsConditionsEnabled && (
				<TermsAndConditions>{termsConditions}</TermsAndConditions>
			)}
		</CarouselContainer>
	);
};

export default NewCarousel;
